import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Repeat,
  ChevronDown,
  CheckCircle,
  ShieldCheck,
  Lock,
  Clock3,
  Building2,
  Users,
  CreditCard,
  Wrench,
  MessageSquare,
  FileText,
  BarChart3,
  CalendarDays,
  ArrowRight,
  Zap,
  Smartphone,
  Search,
  Check,
  X,
  TrendingUp,
  Bell,
  Send,
  BriefcaseBusiness,
  CircleDollarSign,
  Settings2,
  Layers3,
  Sparkles,
} from "lucide-react";

const CheckIcon = ({
  className = "h-6 w-5 flex-none text-gray-400",
}) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
      clipRule="evenodd"
    />
  </svg>
);

const FeatureIcon = ({ icon: Icon }) => (
  <div className="h-12 w-12 rounded-xl bg-[#AF9B8A]/10 border border-[#AF9B8A]/20 flex items-center justify-center flex-none">
    <Icon className="w-6 h-6 text-[#AF9B8A]" />
  </div>
);

function App() {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const [activePricing, setActivePricing] = useState("enhanced");

  const features = [
    {
      title: "Portfolio Dashboard",
      icon: LayoutDashboard,
      description:
        "See occupancy, rent collection, upcoming renewals and portfolio performance in one place.",
      image:
        "https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6a284c269acc7a54522c9557_Screenshot%202026-06-09%20at%2020.21.07.jpg",
    },
    {
      title: "Tenant Management",
      icon: ClipboardList,
      description:
        "Keep tenant information, documents, tenancy details and important dates organised without digging through folders.",
      image:
        "https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6a284d7d1adc84094d2d04c7_Screenshot%202026-06-09%20at%2020.28.47.jpg",
    },
    {
      title: "Payment Insights & Analytics",
      icon: CreditCard,
      description:
        "Track rent and financial performance with a clear view of what's coming in, what's going out and how your portfolio is performing.",
      image:
        "https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6a284c269acc7a54522c9557_Screenshot%202026-06-09%20at%2020.21.07.jpg",
    },
    {
      title: "Maintenance",
      icon: Wrench,
      description:
        "Turn maintenance requests into organised jobs, communicate with contractors and keep a record from report to completion.",
      image:
        "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    },
    {
      title: "Automations",
      icon: Repeat,
      description:
        "Automate reminders, rent tracking, compliance deadlines and recurring tasks so less of your day disappears into admin.",
      image:
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80",
    },
  ];

  const faqs = [
    {
      q: "What is Beem?",
      a: "Beem is an all-in-one property management platform designed to give landlords and property managers one place to manage properties, tenants, documents, payments, maintenance and portfolio performance.",
    },
    {
      q: "Is there really a free plan?",
      a: "Yes. Beem Free is free forever and lets you manage one property with core property management features. There is no time limit.",
    },
    {
      q: "What do I get for £29 per month?",
      a: "Beem Plus gives you everything in Free Forever, expanded contact allowances, up to 5 properties, analytics and payment insights. It's designed for landlords who are ready to manage their portfolio more seriously without jumping into a larger system.",
    },
    {
      q: "What's included in Beem Enhanced?",
      a: "Beem Enhanced is £69 per month and includes everything in Plus, up to 20 properties, the tenant portal, maintenance jobs, contractor SMS links, quotes, scheduling and proof of work.",
    },
    {
      q: "What if I have more than 20 properties?",
      a: "No problem. For larger portfolios we offer bespoke pricing based on your requirements. Contact us and we'll work out the right setup for your portfolio.",
    },
    {
      q: "Do I need a credit card to start?",
      a: "No. You can start with Beem Free without entering payment details.",
    },
    {
      q: "Can I upgrade later?",
      a: "Absolutely. Start with one property and move to Plus or Pro when your portfolio grows and you need more from Beem.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes. There are no long-term contracts. You can cancel your paid plan whenever you choose.",
    },
    {
      q: "Is my data secure?",
      a: "Beem is built with security and controlled access in mind. Your property and tenant information is kept within your Beem account and access can be managed appropriately.",
    },
  ];

  const pricingPlans = [
    {
      id: "free",
      name: "Free",
      eyebrow: "FREE FOREVER",
      price: "£0",
      suffix: "/month",
      description:
        "Everything you need to get your first property organised.",
      cta: "Start for free",
      dark: false,
      features: [
        "1 property",
        "Limited contacts",
        "Core property management",
        "Property & tenancy records",
        "Document storage",
        "Mobile & desktop access",
      ],
    },
    {
      id: "plus",
      name: "Plus",
      eyebrow: "FOR GROWING LANDLORDS",
      price: "£29",
      suffix: "/month",
      description:
        "More properties, better visibility and less financial admin.",
      cta: "Start with Plus",
      dark: true,
      features: [
        "Everything in Free",
        "Up to 5 properties",
        "Increased contact allowance",
        "Portfolio analytics",
        "Payments",
        "Financial visibility",
        "More room to grow",
      ],
    },
    {
      id: "enhanced",
      name: "Enhanced",
      eyebrow: "MOST POWERFUL",
      price: "£69",
      suffix: "/month",
      description:
        "The complete Beem experience for serious landlords and managers.",
      cta: "Start with Enhanced",
      dark: true,
      popular: true,
      features: [
        "Everything in Plus",
        "Up to 20 properties",
        "Tenant portal",
        "Maintenance jobs",
        "Contractor SMS links",
        "Quotes & scheduling",
        "Proof of work",
      ],
    },
  ];

  return (
<div className="text-white overflow-hidden overflow-x-hidden -mt-[90px] w-full max-w-full">
  {/* =========================================================
      HERO
  ========================================================== */}
  <section className="relative min-h-screen w-full overflow-hidden">
    <div className="absolute inset-0 w-full max-w-full overflow-hidden">
      <img
        src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
        className="w-full h-full object-cover"
        alt=""
      />
      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
    </div>

    <div className="relative z-20 flex items-center min-h-screen px-6 md:px-12 w-full max-w-full">
      <div className="max-w-6xl w-full mx-auto lg:pt-[12vh]">
        <div className="max-w-4xl">

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-thin leading-[0.98] tracking-tight">
            Your property portfolio.
            <br />
            <span className="text-[#AF9B8A] font-semibold">
              Finally under control.
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed">
            Beem brings properties, tenants, documents, payment insights,
            maintenance and performance together in one modern platform.
          </p>

          <div className="mt-10 flex gap-4 flex-wrap">
            <Link
              to="/sign-up"
              className="group bg-[#AF9B8A] text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-[#b9a795] transition flex items-center gap-2"
            >
              Start for free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>

            <a
              href="#how-it-works"
              className="bg-white/5 border border-white/20 backdrop-blur-md text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-white/10 transition"
            >
              See how Beem works
            </a>
          </div>


        </div>


      </div>
    </div>
  </section>


      {/* =========================================================
          PROBLEM / SOLUTION
      ========================================================== */}
      <section className="py-28 px-6 bg-white text-black" id="demo">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
                Property management shouldn't feel like this
              </p>

              <h2 className="mt-5 text-5xl md:text-6xl font-bold tracking-tight">
                Stop managing your portfolio from
                <span className="text-[#AF9B8A]"> ten different places.</span>
              </h2>

              <p className="mt-7 text-lg text-gray-600 max-w-xl leading-relaxed">
                Spreadsheets for finances. WhatsApp for tenants. Email for
                contractors. Folders for documents. Notes for reminders.
              </p>

              <p className="mt-5 text-lg text-gray-600 max-w-xl leading-relaxed">
                Beem puts the important stuff together so you can see what is
                happening across your portfolio without hunting for it.
              </p>

              <div className="mt-8 flex items-center gap-3">
                <div className="h-px w-12 bg-[#AF9B8A]" />
                <span className="text-sm font-semibold text-gray-500">
                  One system. One view.
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  icon: FileText,
                  title: "Documents",
                  text: "Keep property and tenancy information organised.",
                },
                {
                  icon: Users,
                  title: "Tenants",
                  text: "Know who is where and what needs attention.",
                },
                {
                  icon: CreditCard,
                  title: "Payments",
                  text: "Keep financial information visible and accessible.",
                },
                {
                  icon: Wrench,
                  title: "Maintenance",
                  text: "Turn problems into trackable jobs.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-3xl border border-black/10 p-7 bg-gray-50 hover:shadow-xl transition-shadow"
                >
                  <FeatureIcon icon={item.icon} />
                  <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-2 text-gray-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          HOW IT WORKS
      ========================================================== */}
      <section
        id="how-it-works"
        className="py-28 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] text-black/80"
      >
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
              How Beem works
            </p>

            <h2 className="mt-5 text-5xl md:text-6xl font-bold tracking-tight text-white/80">
              From scattered admin to
              <span className="text-[#AF9B8A]"> one clear workflow.</span>
            </h2>

            <p className="mt-6 text-lg text-white/65">
              Beem gives every part of your property management operation a
              place to live.
            </p>
          </div>

          <div className="mt-20 grid md:grid-cols-4 gap-5">
            {[
              {
                number: "01",
                title: "Add your properties",
                text: "Create a clear digital record for each property and keep the important information together.",
              },
              {
                number: "02",
                title: "Manage your tenants",
                text: "Keep tenancy information, contacts, documents and important details accessible.",
              },
              {
                number: "03",
                title: "Handle what happens",
                text: "Track maintenance, communicate with contractors and keep jobs moving.",
              },
              {
                number: "04",
                title: "See the bigger picture",
                text: "Use analytics and financial visibility to understand how your portfolio is performing.",
              },
            ].map((step) => (
              <div
                key={step.number}
                className="relative rounded-3xl bg-white border border-black/10 p-7 hover:-translate-y-1 transition-transform"
              >
                <span className="text-sm font-bold text-[#AF9B8A]">
                  {step.number}
                </span>

                <h3 className="mt-6 text-xl font-bold">{step.title}</h3>

                <p className="mt-3 text-gray-600 leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          MAINTENANCE FEATURE
      ========================================================== */}
      <section className="py-28 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
                Beem Enhanced
              </p>

              <h2 className="mt-5 text-5xl md:text-6xl font-bold">
                Maintenance without
                <span className="text-[#AF9B8A]"> the back-and-forth.</span>
              </h2>

              <p className="mt-7 text-lg text-white/65 leading-relaxed max-w-xl">
                A tenant reports an issue. Instead of messages disappearing
                into WhatsApp threads and email chains, turn it into a proper
                maintenance job inside Beem.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  {
                    icon: MessageSquare,
                    title: "Capture the issue",
                    text: "Keep the problem attached to the right property and tenancy.",
                  },
                  {
                    icon: Send,
                    title: "Contact the right contractor",
                    text: "Send contractor links and job information without rebuilding the conversation.",
                  },
                  {
                    icon: CalendarDays,
                    title: "Schedule the work",
                    text: "Keep quotes, scheduling and job progress in one place.",
                  },
                  {
                    icon: CheckCircle,
                    title: "Close the loop",
                    text: "Store proof of work so there is a record when the job is complete.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-lg bg-[#AF9B8A]/10 border border-[#AF9B8A]/20 flex items-center justify-center flex-none">
                      <item.icon className="w-5 h-5 text-[#AF9B8A]" />
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg">{item.title}</h3>
                      <p className="text-white/50 mt-1">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-10 bg-[#AF9B8A]/10 blur-3xl rounded-full" />

              <div className="relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 shadow-2xl">
                <div className="rounded-2xl bg-[#111] border border-white/10 overflow-hidden">
                  <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-white/40 uppercase tracking-widest">
                        Maintenance job
                      </p>
                      <h3 className="font-semibold mt-1">
                        Boiler not heating
                      </h3>
                    </div>

                    <span className="rounded-full bg-[#AF9B8A]/10 text-[#AF9B8A] border border-[#AF9B8A]/20 px-3 py-1 text-xs">
                      In progress
                    </span>
                  </div>

                  <div className="p-5 space-y-4">
                    {[
                      ["Property", "14 Riverside Road"],
                      ["Tenant", "Sarah Williams"],
                      ["Contractor", "Northside Heating"],
                      ["Quote", "£185.00"],
                      ["Scheduled", "Tuesday · 10:30"],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex items-center justify-between border-b border-white/5 pb-4"
                      >
                        <span className="text-sm text-white/40">{label}</span>
                        <span className="text-sm text-white/85">{value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="p-5 bg-white/[0.03]">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#AF9B8A] flex items-center justify-center">
                        <Check className="w-4 h-4 text-black" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">
                          Job tracked in Beem
                        </p>
                        <p className="text-xs text-white/40">
                          Proof of work can be added when complete
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          FINANCIAL / ANALYTICS
      ========================================================== */}
      <section className="py-28 px-6 bg-white text-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl border border-black/10 bg-gray-50 p-5 shadow-xl">
                <div className="rounded-2xl bg-white border border-black/5 p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">
                        Portfolio overview
                      </p>
                      <p className="text-3xl font-bold mt-2">£8,420</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Monthly rent
                      </p>
                    </div>

                    <div className="h-10 w-10 rounded-xl bg-[#AF9B8A]/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#AF9B8A]" />
                    </div>
                  </div>

                  <div className="mt-10 h-40 flex items-end gap-3">
                    {[35, 48, 43, 61, 55, 74, 82, 78, 91, 84, 94, 100].map(
                      (height, index) => (
                        <div
                          key={index}
                          className="flex-1 rounded-t-md bg-[#AF9B8A]/70"
                          style={{ height: `${height}%` }}
                        />
                      )
                    )}
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-gray-50 p-4">
                      <p className="text-xs text-gray-500">Collected</p>
                      <p className="font-bold mt-1">96%</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <p className="text-xs text-gray-500">Properties</p>
                      <p className="font-bold mt-1">12</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <p className="text-xs text-gray-500">Occupied</p>
                      <p className="font-bold mt-1">100%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
                Know your portfolio
              </p>

              <h2 className="mt-5 text-5xl md:text-6xl font-bold tracking-tight">
                Stop guessing how your properties are doing.
              </h2>

              <p className="mt-7 text-lg text-gray-600 leading-relaxed">
                Beem turns your property information into a clearer picture of
                your portfolio. Track rent, payments, performance and trends
                without building your own spreadsheet system.
              </p>

              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                {[
                  "Portfolio performance",
                  "Rent visibility",
                  "Payment tracking",
                  "Property-level information",
                ].map((item) => (
                  <div key={item} className="flex gap-3 items-center">
                    <CheckCircle className="w-5 h-5 text-[#AF9B8A]" />
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-sm text-gray-500">
                Analytics and payment insights are included with Beem Plus and above.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          TENANT EXPERIENCE
      ========================================================== */}
      <section className="py-28 px-6 bg-gray-50 text-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
              Beem Enhanced
            </p>

            <h2 className="mt-5 text-5xl md:text-6xl font-bold">
              Give your tenants a better experience too.
            </h2>

            <p className="mt-6 text-lg text-gray-600">
              With the Beem tenant portal, important information has somewhere
              to go. Tenants can see their tenancy information, documents, payments and maintenance requests without having to ask you for it.
            </p>
          </div>

          <div className="mt-16 grid lg:grid-cols-3 gap-6">
            {[
              {
                title: "A dedicated portal",
                text: "Give tenants a clear place to access the information relevant to their tenancy.",
              },
              {
                title: "Fewer unnecessary messages",
                text: "Put useful information where tenants can find it instead of answering the same questions repeatedly.",
              },
              {
                title: "Maintenance requests",
                text: "Let maintenance become an organised process rather than another message lost in a chat.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-3xl p-8 border border-black/10"
              >
                <h3 className="text-2xl font-bold mt-7">{item.title}</h3>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          BEEM ECOSYSTEM
      ========================================================== */}
      <section className="py-28 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
                Built around your workflow
              </p>

              <h2 className="mt-5 text-5xl md:text-6xl font-bold">
                Your portfolio is more than a list of properties.
              </h2>

              <p className="mt-7 text-lg text-white/60 max-w-2xl leading-relaxed">
                Every property has tenants, documents, payments, maintenance,
                dates and decisions attached to it. Beem connects those pieces
                so you don't have to.
              </p>
            </div>


          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Building2,
                title: "Properties",
                text: "Create a single source of truth for every property in your portfolio.",
              },
              {
                icon: Users,
                title: "People",
                text: "Keep tenants, contacts and contractors connected to the right places.",
              },
              {
                icon: FileText,
                title: "Documents",
                text: "Keep contracts, records and important files organised and accessible.",
              },
              {
                icon: CreditCard,
                title: "Money",
                text: "Understand rent and portfolio performance without spreadsheet gymnastics.",
              },
              {
                icon: Wrench,
                title: "Maintenance",
                text: "Track jobs from initial issue through to completion and proof of work.",
              },
              {
                icon: BarChart3,
                title: "Insights",
                text: "See the information that helps you make better portfolio decisions.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-white/10 bg-white/5 p-7 hover:bg-white/[0.08] transition"
              >
                <item.icon className="w-7 h-7 text-[#AF9B8A]" />
                <h3 className="text-xl font-semibold mt-6">{item.title}</h3>
                <p className="text-white/50 mt-3 leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          VIDEO / CTA
      ========================================================== */}
      <section className="py-28 px-6 bg-white text-black">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold tracking-wide text-[#AF9B8A] uppercase">
              Are you ready to take control?
            </p>

            <h2 className="mt-4 text-5xl font-bold tracking-tight">
              Your whole portfolio.
              <br />
              <span className="text-[#AF9B8A]">One view.</span>
            </h2>

            <p className="mt-6 text-gray-600 text-lg leading-relaxed">
              Beem gives landlords complete visibility and control over their
              property portfolio without the usual admin headache.
            </p>

            <p className="mt-4 text-gray-600 leading-relaxed">
              From tracking rent and managing tenants to handling documents,
              maintenance and portfolio performance, Beem brings the operation
              together.
            </p>

            <div className="mt-8">
              <Link
                to="/sign-up"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-black font-bold shadow hover:shadow-xl transition bg-gradient-to-r from-[#AF9B8A] to-[#e9dbc7]"
              >
                Start free
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="relative w-full">
            <div className="relative w-full overflow-hidden rounded-2xl bg-black/40 backdrop-blur-md border border-black/10 shadow-xl aspect-video flex items-center justify-center">
              <iframe
                className="w-full h-full"
                src="https://player.vimeo.com/video/1180900588?badge=0&autopause=0&player_id=0&app_id=58479"
                title="Beem Demo"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          PRICING INTRO
      ========================================================== */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b]">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
            Simple pricing
          </p>

          <h2 className="mt-5 text-5xl md:text-6xl font-bold">
            Start small.
            <br />
            <span className="text-[#AF9B8A]">Scale at your pace.</span>
          </h2>

          <p className="mt-7 text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            You shouldn't have to pay for a huge property management system
            before you've built a huge portfolio. Start free and upgrade when
            Beem becomes part of how you run your business.
          </p>

          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            {[
              ["free", "1 property"],
              ["plus", "Up to 5"],
              ["enhanced", "Up to 20"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => {
                  setActivePricing(id);
                  document
                    .getElementById(`pricing-${id}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className={`px-5 py-2.5 rounded-full border transition ${
                  activePricing === id
                    ? "bg-[#AF9B8A] border-[#AF9B8A] text-white"
                    : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          PRICING
      ========================================================== */}
      <section
        id="plans"
        className="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8 text-black"
      >
        <div
          className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl"
          aria-hidden="true"
        >
          <div
            className="mx-auto aspect-[1155/678] w-[1155px] bg-gradient-to-tr from-[#AF9B8A] to-[#e9dbc7] opacity-30"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>

        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium sm:text-xl text-[#AF9B8A]">
            Plans that grow with you
          </h2>

          <p className="text-5xl md:text-6xl font-bold mb-6 text-black text-center">
            Find the plan for you
          </p>

          <p className="max-w-2xl mx-auto text-gray-600 text-lg">
            Whether you're managing your first property or running a growing
            portfolio, there's a Beem plan for you.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-4">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              id={`pricing-${plan.id}`}
              className={`relative rounded-3xl p-8 shadow-xl transition-all duration-300 ${
                plan.dark
                  ? "bg-gray-900 text-white"
                  : "bg-white text-black ring-1 ring-gray-900/10"
              } ${
                activePricing === plan.id
                  ? "ring-2 ring-[#AF9B8A] -translate-y-1"
                  : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-6 rounded-full bg-[#AF9B8A] px-4 py-1 text-xs font-bold text-white">
                  MOST POPULAR
                </div>
              )}

              <p
                className={`text-xs font-bold tracking-widest ${
                  plan.dark ? "text-[#AF9B8A]" : "text-[#AF9B8A]"
                }`}
              >
                {plan.eyebrow}
              </p>

              <h3 className="mt-4 text-2xl font-bold">{plan.name}</h3>

              <p className="mt-5 flex items-baseline gap-x-2">
                <span className="text-5xl font-semibold tracking-tight">
                  {plan.price}
                </span>
                <span
                  className={`text-sm ${
                    plan.dark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {plan.suffix}
                </span>
              </p>

              <p
                className={`mt-5 text-sm leading-relaxed min-h-[48px] ${
                  plan.dark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {plan.description}
              </p>

              <ul
                className={`mt-8 space-y-3 text-sm ${
                  plan.dark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon
                      className={`h-6 w-5 flex-none ${
                        plan.dark
                          ? "text-[#AF9B8A]"
                          : "text-[#AF9B8A]"
                      }`}
                    />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                to="/sign-up"
                onClick={() => setActivePricing(plan.id)}
                className={`mt-10 block rounded-xl px-4 py-3 text-center text-sm font-semibold transition ${
                  plan.dark
                    ? "bg-[#AF9B8A] text-white hover:bg-[#b9a795]"
                    : "border border-black/20 text-black hover:border-[#AF9B8A]"
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}

          {/* Bespoke */}
          <div
            id="pricing-bespoke"
            className="relative rounded-3xl bg-gradient-to-br from-[#111] to-[#292929] text-white p-8 shadow-xl border border-white/10 overflow-hidden"
          >
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#AF9B8A]/10 blur-2xl" />

            <div className="relative">
              <p className="text-xs font-bold tracking-widest text-[#AF9B8A]">
                20+ PROPERTIES
              </p>

              <h3 className="mt-4 text-2xl font-bold">
                Bespoke
              </h3>

              <p className="mt-5 text-3xl font-semibold">
                Let's talk
              </p>

              <p className="mt-5 text-sm text-gray-400 leading-relaxed">
                Managing a larger portfolio? We'll build a pricing structure
                around the size and requirements of your operation.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-gray-300">
                {[
                  "20+ properties",
                  "Tailored pricing",
                  "Designed around your portfolio",
                  "Talk directly with the Beem team",
                ].map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-[#AF9B8A]" />
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href="mailto:wjelson@beemtech.co.uk"
                className="mt-10 block rounded-xl bg-white text-black px-4 py-3 text-center text-sm font-semibold hover:bg-gray-100 transition"
              >
                Contact us
              </a>
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          Start with Free. Upgrade when you need more.
        </p>
      </section>

      {/* =========================================================
          PLAN COMPARISON
      ========================================================== */}
      <section className="py-28 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] text-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
              Compare plans
            </p>

            <h2 className="mt-5 text-5xl md:text-6xl font-bold text-white/80">
              Know exactly what you're getting.
            </h2>

            <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto">
              No confusing feature tiers. As your portfolio grows, Beem gives
              you more tools to manage it properly.
            </p>
          </div>

          <div className="mt-16 overflow-x-auto rounded-3xl border border-black/10 bg-white shadow-xl">
            <table className="w-full min-w-[850px] border-collapse">
              <thead>
                <tr className="border-b border-black/10">
                  <th className="text-left p-6 font-semibold">Feature</th>
                  <th className="p-6 text-center">
                    <span className="text-[#AF9B8A]">Free</span>
                    <div className="text-xs text-gray-400 font-normal mt-1">
                      £0 forever
                    </div>
                  </th>
                  <th className="p-6 text-center">
                    <span className="text-[#AF9B8A]">Plus</span>
                    <div className="text-xs text-gray-400 font-normal mt-1">
                      £29/month
                    </div>
                  </th>
                  <th className="p-6 text-center">
                    <span className="text-[#AF9B8A]">Enhanced</span>
                    <div className="text-xs text-gray-400 font-normal mt-1">
                      £69/month
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {[
                  ["Properties", "1", "Up to 5", "Up to 20"],
                  ["Core property management", true, true, true],
                  ["Document storage", true, true, true],
                  ["Contacts", "Limited", "Increased", "Increased"],
                  ["Analytics", false, true, true],
                  ["Payment tracking", false, true, true],
                  ["Tenant portal", false, false, true],
                  ["Maintenance jobs", false, false, true],
                  ["Contractor SMS links", false, false, true],
                  ["Quotes & scheduling", false, false, true],
                ].map(([feature, free, plus, enhanced], index) => (
                  <tr
                    key={feature}
                    className={`border-b border-black/5 ${
                      index % 2 === 0 ? "bg-gray-50/60" : ""
                    }`}
                  >
                    <td className="p-5 font-medium">{feature}</td>

                    {[free, plus, enhanced].map((value, i) => (
                      <td
                        key={i}
                        className="p-5 text-center text-sm text-gray-600"
                      >
                        {typeof value === "boolean" ? (
                          value ? (
                            <CheckCircle className="w-5 h-5 text-[#AF9B8A] mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          value
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* =========================================================
          WHO IT'S FOR
      ========================================================== */}
      <section className="py-28 px-6 bg-white text-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
                Built for real portfolios
              </p>

              <h2 className="mt-5 text-5xl md:text-6xl font-bold">
                Whether you've got one property or a growing operation.
              </h2>

              <p className="mt-7 text-lg text-gray-600 leading-relaxed">
                Beem is designed to grow with the way you manage property.
                Start with the essentials and unlock more powerful tools as
                your portfolio gets bigger.
              </p>

              <Link
                to="/sign-up"
                className="mt-8 inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
              >
                Get started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: BriefcaseBusiness,
                  title: "Private landlords",
                  text: "Keep your properties organised without paying for a system built for huge agencies.",
                },
                {
                  icon: TrendingUp,
                  title: "Growing investors",
                  text: "Get the visibility you need as your portfolio moves beyond one or two properties.",
                },
                {
                  icon: Settings2,
                  title: "Property managers",
                  text: "Bring tenants, maintenance, documents and portfolio operations into one place.",
                },
                {
                  icon: Layers3,
                  title: "Larger portfolios",
                  text: "Talk to Beem about a bespoke setup once you're managing more than 20 properties.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-5 p-6 rounded-2xl border border-black/10 hover:shadow-lg transition"
                >
                  <FeatureIcon icon={item.icon} />

                  <div>
                    <h3 className="font-bold text-xl">{item.title}</h3>
                    <p className="mt-2 text-gray-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CTA
      ========================================================== */}
      <section className="py-28 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b]">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
            Your portfolio deserves better
          </p>

          <h2 className="mt-5 text-5xl md:text-7xl font-bold tracking-tight">
            One system.
            <br />
            <span className="text-[#AF9B8A]">More control.</span>
          </h2>

          <p className="mt-7 text-lg text-white/60 max-w-2xl mx-auto">
            Start managing your properties with Beem today. Start free and
            upgrade when you're ready.
          </p>

          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link
              to="/sign-up"
              className="group inline-flex items-center gap-2 bg-[#AF9B8A] text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-[#b9a795] transition"
            >
              Start for free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>

            <a
              href="mailto:wjelson@beemtech.co.uk"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-white/10 transition"
            >
              Talk to Beem
            </a>
          </div>

          <p className="mt-6 text-sm text-white/30">
            Free forever · No credit card required · Upgrade when you need to
          </p>
        </div>
      </section>

      {/* =========================================================
          FAQ
      ========================================================== */}
      <section
        id="faqs"
        className="py-24 px-6 bg-gradient-to-br from-[#111] via-[#1a1a1a]"
      >
        <div className="mx-auto max-w-6xl px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm font-semibold tracking-wide text-[#AF9B8A] uppercase">
              FAQs
            </p>

            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-white">
              Everything you need to know
            </h2>

            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              Still have questions? We've answered the common things landlords
              and property managers ask before getting started with Beem.
            </p>

            <div className="mt-10 w-16 h-[2px] bg-[#AF9B8A]" />

            <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6">
              <p className="text-sm text-white/40">Still not sure?</p>

              <p className="mt-2 font-semibold text-white">
                We're happy to help you choose.
              </p>

              <a
                href="mailto:wjelson@beemtech.co.uk"
                className="mt-4 inline-flex items-center gap-2 text-[#AF9B8A] text-sm font-semibold"
              >
                Contact us
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="divide-y divide-white/10 border-t border-white/10">
            {faqs.map((faq, idx) => (
              <div key={idx} className="py-6 group">
                <button
                  onClick={() =>
                    setOpenIndex(openIndex === idx ? null : idx)
                  }
                  className="flex w-full items-start justify-between text-left gap-6"
                >
                  <span className="text-lg font-medium text-white group-hover:text-[#AF9B8A] transition-colors">
                    {faq.q}
                  </span>

                  <ChevronDown
                    className={`h-5 w-5 text-white/60 mt-1 transition-all duration-300 flex-none ${
                      openIndex === idx
                        ? "rotate-180 text-[#AF9B8A]"
                        : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-all duration-300 ${
                    openIndex === idx
                      ? "grid-rows-[1fr] mt-4"
                      : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-white/60 pr-8 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          FINAL PRICING / CTA
      ========================================================== */}
      <section className="relative overflow-hidden bg-white text-black px-6 py-28">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#AF9B8A]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-[#AF9B8A] uppercase">
            Ready when you are
          </p>

          <h2 className="mt-5 text-5xl md:text-7xl font-bold tracking-tight">
            Your next property deserves
            <span className="text-[#AF9B8A]"> Beem.</span>
          </h2>

          <p className="mt-7 text-lg text-gray-600 max-w-2xl mx-auto">
            Start with one property for free. When your portfolio grows, Beem
            grows with you.
          </p>

          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link
              to="/sign-up"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold shadow hover:shadow-xl transition bg-gradient-to-r from-[#AF9B8A] to-[#e9dbc7]"
            >
              Start for free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>

            <a
              href="#plans"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-black/15 font-semibold hover:border-[#AF9B8A] transition"
            >
              View pricing
            </a>
          </div>

          <div className="mt-10 flex justify-center flex-wrap gap-x-8 gap-y-3 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#AF9B8A]" />
              Free forever
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#AF9B8A]" />
              No credit card
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#AF9B8A]" />
              Upgrade anytime
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
